import {
  ARTIFACTS_PATH,
  StoryRoleSchema,
  type CandidateResource,
  type ScreenshotItemResource,
  type StoryBeatResource,
} from '@studio/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { materialName } from '../../app/material.js';
import { RepeatFor } from '../../bulk/RepeatFor.js';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link, useNavigate, type Surface } from '../../shell/router.js';
import { useAsync } from '../../shell/useAsync.js';
import { Composer, type ComposerScope } from '../Composer.js';
import { Lineage, parentOf } from '../Lineage.js';
import { useExperiment } from '../useExperiment.js';
import { moveItem } from './reorder.js';

/** The Refine stage without a Candidate named: which complete set to work on. */
function RefineIndex({ experimentId }: { experimentId: string }): ReactNode {
  const open = useExperiment(experimentId);
  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (open.detail === null) return <section className="page" aria-busy="true" />;

  const ready = open.detail.candidates.filter((candidate) => !candidate.hidden);

  return (
    <section className="page">
      <p className="eyebrow">Refine</p>
      <h1>Откройте готовый набор, чтобы работать с ним.</h1>
      {ready.length === 0 ? (
        <EmptyState
          title="Пока нечего править."
          note="Refine работает с готовым Candidate. Сначала сгенерируйте набор, затем откройте любой."
          action={
            <Link to={`/experiments/${experimentId}/explore`} className="primary-link">
              К Explore
            </Link>
          }
        />
      ) : (
        <ul className="candidate-shelf" aria-label="Candidate для Refine">
          {ready.map((candidate) => (
            <li key={candidate.id}>
              <Link to={`/experiments/${experimentId}/refine/${candidate.id}`} className="secondary-link">
                {candidate.items.length > 0 ? `Открыть набор ${candidate.headline}` : `Открыть ${candidate.headline}`}
              </Link>
              <p className="note">{candidate.summary}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The Story ribbon. A large read of the selected frame, the whole set beside it in
 * order, and one composer whose scope follows the selection. Reordering is a drag and
 * an equally direct pair of controls, because a drag alone is not reachable.
 */
function Story({ experimentId, candidateId }: { experimentId: string; candidateId: string }): ReactNode {
  const open = useExperiment(experimentId);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidate = open.detail?.candidates.find((one) => one.id === candidateId) ?? null;
  const items = candidate?.items ?? [];

  // Every reorder goes through here, and the two controls are the only ways in: a
  // second, undiscoverable rule on a hidden key is the same rule kept in two places.
  const editBeat = useCallback(
    async (index: number, change: Partial<StoryBeatResource>) => {
      const plan = open.detail?.candidates.find((one) => one.id === candidateId)?.storyPlan ?? null;
      if (plan === null) return;
      setError(null);
      try {
        await api.setStoryPlan(
          candidateId,
          plan.beats.map((beat) => (beat.index === index ? { ...beat, ...change } : beat)),
        );
        open.refresh();
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Story Plan не удалось сохранить.');
      }
    },
    [candidateId, open],
  );

  const reorder = useCallback(
    async (from: number, to: number) => {
      const next = moveItem(items, from, to);
      if (next === items) return;
      setError(null);
      try {
        await api.reorderItems(candidateId, next.map((item) => item.id));
        setSelected(to);
        open.refresh();
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Порядок не удалось сохранить.');
      }
    },
    [candidateId, items, open],
  );

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (open.detail === null) return <section className="page" aria-busy="true" />;
  if (candidate === null) {
    return (
      <section className="page">
        <EmptyState
          title="Этого Candidate нет в этом Experiment."
          note="Возможно, он сгенерирован в другом месте, или адрес устарел."
          action={
            <Link to={`/experiments/${experimentId}/explore`} className="primary-link">
              К Explore
            </Link>
          }
        />
      </section>
    );
  }

  const focus = selected === null ? null : (items[selected] ?? null);
  const appId = open.detail.experiment.appId;
  const scope: ComposerScope =
    focus === null
      ? { kind: 'candidate', candidateId: candidate.id, label: candidate.headline, appId }
      : { kind: 'slide', itemId: focus.id, label: `слайд ${(selected ?? 0) + 1}`, appId };
  const parent = parentOf(open.detail.candidates, candidate);

  return (
    <section className="page story">
      <div className="page-head">
        <div>
          <p className="eyebrow">{`Refine · ${candidate.headline}`}</p>
          <h1>Читайте слева направо.</h1>
          {/* R060: which idea this one grew out of, where the set is actually read. */}
          <Lineage candidate={candidate} parent={parent} experimentId={experimentId} />
          <p className="note">
            Каждое принятое изменение создаёт готовый дочерний Candidate. Здесь ничего не правят на месте.
          </p>
        </div>
        <Link to={`/experiments/${experimentId}/explore`} className="secondary-link">
          Все Candidate
        </Link>
      </div>

      {error === null ? null : <Failure message={error} />}

      {/*
        R077 names this affordance `Adapt set`, and names what it opens: the compact
        `Repeat for…` preflight for the new Target. The destination is a separate
        Experiment; the story intent and its order arrive as a reference, so what is
        adapted there is the in-graphic language, not the sequence.
      */}
      <RepeatFor
        label="Адаптировать набор"
        experimentId={experimentId}
        candidateId={candidate.id}
        note="Отдельный Experiment для нового Target. Этот набор приходит как ссылка, из которой читают сюжетный замысел и порядок; адаптируются язык на графике, акценты и культурная рамка."
      />

      <div className="story-layout">
        <StoryPlan candidate={candidate} />

        <div className="story-board">
          <div className="story-viewer">
            {focus === null ? (
              <p className="note">Ничего не выбрано. Composer направляет весь набор.</p>
            ) : (
              <Slide item={focus} className="frame frame-large" />
            )}
          </div>

          {items.length === 0 ? (
            <EmptyState
              title="У этого Candidate нет слайдов."
              note="Как историю читается только набор скриншотов. Candidate из одного изображения правится целиком."
            />
          ) : (
            <ol className="story-ribbon" aria-label="Слайды по порядку">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className={selected === index ? 'slide selected' : 'slide'}
                  draggable
                  onDragStart={() => setDragging(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragging !== null) void reorder(dragging, index);
                    setDragging(null);
                  }}
                >
                  <button
                    type="button"
                    className="slide-select"
                    aria-pressed={selected === index}
                    onClick={() => setSelected(selected === index ? null : index)}
                  >
                    {`Выбрать слайд ${index + 1} · ${item.storyRole}`}
                  </button>
                  <Slide item={item} className="frame" />
                  <SlideSource
                    item={item}
                    position={index}
                    appId={appId}
                    onReplaced={(descendant) => {
                      setError(null);
                      open.refresh();
                      // Nothing is edited in place, so the corrected set is a different
                      // Candidate. Staying on the parent would show the old slide back.
                      navigate(`/experiments/${experimentId}/refine/${descendant}`);
                    }}
                    onFailed={(message) => setError(message)}
                  />
                  <Beat
                    beat={candidate.storyPlan?.beats.find((one) => one.index === index) ?? null}
                    position={index}
                    fallback={item.copy}
                    onChange={(change) => void editBeat(index, change)}
                  />
                  <div className="row-actions">
                    <button
                      type="button"
                      className="ghost"
                      disabled={index === 0}
                      onClick={() => void reorder(index, index - 1)}
                    >
                      {`Сдвинуть слайд ${index + 1} раньше`}
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={index === items.length - 1}
                      onClick={() => void reorder(index, index + 1)}
                    >
                      {`Сдвинуть слайд ${index + 1} позже`}
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <Composer
        scope={scope}
        onDone={() => {
          open.refresh();
        }}
      />
    </section>
  );
}

function Slide({ item, className }: { item: ScreenshotItemResource; className: string }): ReactNode {
  return (
    <img
      className={className}
      src={`${ARTIFACTS_PATH}/${item.artifactId}`}
      alt={`${item.storyRole}: ${item.copy}`}
      loading="lazy"
    />
  );
}

/**
 * R071. Where a slide came from, and how it is corrected.
 *
 * The source was readable only as a word inside the Story Plan, and replacing it was a
 * shape the server had accepted since task 07 with nothing sending it: naming App
 * Library material with no instruction puts those exact bytes in the slide, writes the
 * descendant immediately and buys nothing. That is why this is a select rather than a
 * sentence - there are no weights here and no locks, only which material it is.
 */
function SlideSource({
  item,
  position,
  appId,
  onReplaced,
  onFailed,
}: {
  item: ScreenshotItemResource;
  position: number;
  appId: string;
  onReplaced: (candidateId: string) => void;
  onFailed: (message: string) => void;
}): ReactNode {
  const library = useAsync(async () => api.readLibrary(appId), [appId]);
  const [busy, setBusy] = useState(false);
  const usable = (library.value?.items ?? []).filter((entry) => entry.artifactId !== null);
  const named = usable.find((entry) => entry.id === item.sourceLibraryItemId) ?? null;

  return (
    <div className="slide-source">
      <p className="note">
        {`Источник: ${item.sourceKind}${named === null ? '' : ` · ${materialName(named)}`} · ${item.framing}`}
      </p>
      {usable.length === 0 ? null : (
        <>
          <label htmlFor={`slide-source-${item.id}`} className="note">
            {`Заменить источник слайда ${position + 1}`}
          </label>
          <select
            id={`slide-source-${item.id}`}
            value=""
            disabled={busy}
            onChange={(event) => {
              const libraryItemId = event.target.value;
              if (libraryItemId === '') return;
              setBusy(true);
              void api
                .editSlide(item.id, { materialIds: [libraryItemId] })
                .then((response) => {
                  // The discriminant is in the body: naming material with no instruction
                  // is always a replacement, and 201 against 202 says only what it cost.
                  if (response.kind === 'replaced') onReplaced(response.candidate.id);
                })
                .catch((caught: unknown) =>
                  onFailed(caught instanceof ApiError ? caught.message : 'Этот материал не удалось использовать.'),
                )
                .finally(() => setBusy(false));
            }}
          >
            <option value="">Оставить как есть</option>
            {usable.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {materialName(entry)}
              </option>
            ))}
          </select>
          <p className="note">Здесь ничего не генерируется: байты уже здесь.</p>
        </>
      )}
    </div>
  );
}

/**
 * One beat, on the slide it belongs to. It is context the operator can correct where
 * they are reading it: there is no plan form to open and nothing to approve, and a
 * Candidate without a plan simply shows the copy that is on the frame.
 */
function Beat({
  beat,
  position,
  fallback,
  onChange,
}: {
  beat: StoryBeatResource | null;
  position: number;
  fallback: string;
  onChange: (change: Partial<StoryBeatResource>) => void;
}): ReactNode {
  if (beat === null) return <p className="note">{fallback}</p>;
  return (
    <div className="beat">
      <label htmlFor={`beat-copy-${position}`} className="note">
        {`Story beat ${position + 1}`}
      </label>
      <input
        id={`beat-copy-${position}`}
        key={beat.copy}
        defaultValue={beat.copy}
        onBlur={(event) => {
          if (event.target.value !== beat.copy) onChange({ copy: event.target.value });
        }}
      />
      <label htmlFor={`beat-role-${position}`} className="note">
        {`Роль в истории для слайда ${position + 1}`}
      </label>
      <select
        id={`beat-role-${position}`}
        value={beat.role}
        onChange={(event) => onChange({ role: event.target.value as StoryBeatResource['role'] })}
      >
        {StoryRoleSchema.options.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The whole plan, read end to end. Every beat is corrected on its own slide. */
function StoryPlan({ candidate }: { candidate: CandidateResource }): ReactNode {
  const plan = candidate.storyPlan;
  if (plan === null) {
    return (
      <aside className="story-plan">
        <h2>Story Plan</h2>
        <p className="note">У этого Candidate нет Story Plan. Порядок слайдов и есть история.</p>
      </aside>
    );
  }
  return (
    <aside className="story-plan">
      <h2>Story Plan</h2>
      <p className="note">
        {plan.edited
          ? 'Отредактировано вами. Каждый beat правится на своём слайде.'
          : 'Предложено вместе с набором. Каждый beat правится на своём слайде.'}
      </p>
      <ul aria-label="Story Plan">
        {plan.beats.map((beat) => (
          <li key={beat.index}>
            <strong>{beat.role}</strong>
            <span className="note">{`${beat.copy} · ${beat.sourceKind} · ${beat.framing}`}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export const surfaces: readonly Surface[] = [
  {
    id: 'experiment-refine',
    path: '/experiments/:experimentId/refine',
    title: 'Refine',
    render: (params) => <RefineIndex experimentId={params.experimentId ?? ''} />,
  },
  {
    id: 'experiment-refine-candidate',
    path: '/experiments/:experimentId/refine/:candidateId',
    title: 'Refine',
    render: (params) => <Story experimentId={params.experimentId ?? ''} candidateId={params.candidateId ?? ''} />,
  },
];
